/**
 * Central error translation for user-facing messages.
 *
 * Rules:
 *   1. NEVER leak raw Error.message from libraries (Firestore, fetch, etc.) to the UI.
 *      Map known codes/types to Vietnamese; everything else falls back to a generic line.
 *   2. App-controlled errors thrown from loaders/handlers use `AppError` — its `userMessage`
 *      is considered safe Vietnamese text and is passed through.
 *   3. Always `console.error` the raw error so devs can debug from the browser console.
 */
import * as Sentry from '@sentry/react';
import { authErrorToVietnamese } from './auth';

export interface UserFacingError {
  title: string;
  detail: string;
  /** Stable identifier callers can switch on (e.g. for analytics or alternate UIs). */
  code:
    | 'offline'
    | 'permission'
    | 'not-found'
    | 'unauthenticated'
    | 'timeout'
    | 'cancelled'
    | 'auth'
    | 'app'
    | 'unknown';
  /** True if the user should see a "Thử lại" affordance. */
  canRetry: boolean;
}

/**
 * Throw this from loaders and handlers when you want a specific Vietnamese message
 * to appear in the UI. Anything else thrown will be rendered as a generic error.
 */
export class AppError extends Error {
  readonly userMessage: string;
  readonly userTitle?: string;
  readonly canRetry: boolean;

  constructor(userMessage: string, options: { title?: string; canRetry?: boolean } = {}) {
    super(userMessage);
    this.name = 'AppError';
    this.userMessage = userMessage;
    this.userTitle = options.title;
    this.canRetry = options.canRetry ?? false;
  }
}

/**
 * Thrown by content loaders when Firestore denies an anonymous read (Phase 0 rules require
 * auth to read content). Routes detect it via `isAuthRequiredError` and render the friendly
 * "please sign in" screen instead of the red error card.
 */
export class AuthRequiredError extends AppError {
  constructor(userMessage = 'Đăng nhập để xem nội dung này.') {
    super(userMessage, { title: 'Cần đăng nhập', canRetry: false });
    this.name = 'AuthRequiredError';
  }
}

export function isAuthRequiredError(err: unknown): err is AuthRequiredError {
  return err instanceof AuthRequiredError;
}

/** True for a Firestore permission-denied / unauthenticated error code. */
export function isPermissionDenied(err: unknown): boolean {
  const code = getCode(err);
  return code === 'permission-denied' || code === 'unauthenticated';
}

const GENERIC: UserFacingError = {
  title: 'Đã có lỗi xảy ra',
  detail: 'Vui lòng thử lại sau giây lát.',
  code: 'unknown',
  canRetry: true,
};

const OFFLINE: UserFacingError = {
  title: 'Mất kết nối Internet',
  detail: 'Vui lòng kiểm tra mạng (Wi-Fi / 4G) rồi thử lại.',
  code: 'offline',
  canRetry: true,
};

function getCode(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const c = (err as { code?: unknown }).code;
    if (typeof c === 'string') return c;
  }
  return '';
}

function looksOffline(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const code = getCode(err);
  if (code === 'unavailable' || code === 'auth/network-request-failed') return true;
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  if (!msg) return false;
  return (
    /client is offline/i.test(msg) ||
    /failed to fetch/i.test(msg) ||
    /networkerror/i.test(msg) ||
    /network request failed/i.test(msg)
  );
}

/**
 * Translate any thrown value into a safe, user-facing message.
 * Side effect: logs the raw value via console.error for dev visibility.
 */
export function formatUserError(err: unknown): UserFacingError {
  // Always surface raw error to dev console — never lost.
  try {
    console.error('[error]', err);
  } catch {
    // ignore (e.g. console unavailable)
  }

  // Report to monitoring (no-op unless Sentry was initialized with a DSN). Skip AuthRequired —
  // it's an expected control-flow signal for anonymous users, not a fault.
  if (!(err instanceof AuthRequiredError)) {
    try {
      Sentry.captureException(err);
    } catch {
      // never let monitoring throw into the UI path
    }
  }

  if (err instanceof AppError) {
    return {
      title: err.userTitle ?? 'Không thể tải dữ liệu',
      detail: err.userMessage,
      code: 'app',
      canRetry: err.canRetry,
    };
  }

  if (looksOffline(err)) return OFFLINE;

  const code = getCode(err);

  // Firebase Auth codes — delegate to existing mapper.
  if (code.startsWith('auth/')) {
    return {
      title: 'Không thể xác thực',
      detail: authErrorToVietnamese(err),
      code: 'auth',
      canRetry: code !== 'auth/too-many-requests',
    };
  }

  // Firestore error codes (https://firebase.google.com/docs/reference/js/firestore_.firestoreerror).
  switch (code) {
    case 'permission-denied':
      return {
        title: 'Không có quyền truy cập',
        detail: 'Bạn không có quyền xem dữ liệu này. Hãy đăng nhập lại nếu cần.',
        code: 'permission',
        canRetry: false,
      };
    case 'not-found':
      return {
        title: 'Không tìm thấy dữ liệu',
        detail: 'Mục bạn yêu cầu không tồn tại hoặc đã bị xóa.',
        code: 'not-found',
        canRetry: false,
      };
    case 'unauthenticated':
      return {
        title: 'Phiên đăng nhập đã hết hạn',
        detail: 'Vui lòng đăng nhập lại để tiếp tục.',
        code: 'unauthenticated',
        canRetry: false,
      };
    case 'deadline-exceeded':
    case 'aborted':
      return {
        title: 'Hết thời gian chờ',
        detail: 'Mạng đang chậm. Vui lòng thử lại.',
        code: 'timeout',
        canRetry: true,
      };
    case 'cancelled':
      return {
        title: 'Yêu cầu đã bị hủy',
        detail: 'Hành động vừa rồi bị hủy giữa chừng.',
        code: 'cancelled',
        canRetry: true,
      };
    case 'resource-exhausted':
      return {
        title: 'Quá tải tạm thời',
        detail: 'Hệ thống đang bận. Vui lòng thử lại sau ít phút.',
        code: 'timeout',
        canRetry: true,
      };
  }

  return GENERIC;
}
